import React, { useState, useEffect } from 'react';
import { BarChart3, Download, Calendar, TrendingUp, AlertTriangle } from 'lucide-react';
import { gmudService } from '../services/api';

interface RelatorioData {
  totalGMUDs: number;
  porStatus: { [key: string]: number };
  porImpacto: { [key: string]: number };
  porMes: { [key: string]: number };
  tempoMedioExecucao: number;
}

const Relatorios: React.FC = () => {
  const [relatorioData, setRelatorioData] = useState<RelatorioData | null>(null);
  const [loading, setLoading] = useState(true);
  const [filtroPeriodo, setFiltroPeriodo] = useState('30dias');

  useEffect(() => {
    carregarRelatorios();
  }, [filtroPeriodo]);

  const carregarRelatorios = async () => {
    try {
      setLoading(true);
      const gmuds = await gmudService.list();

      // Processar dados para relatórios
      const data: RelatorioData = {
        totalGMUDs: gmuds.length,
        porStatus: {},
        porImpacto: {},
        porMes: {},
        tempoMedioExecucao: 0,
      };

      gmuds.forEach(gmud => {
        // Contar por status
        data.porStatus[gmud.status] = (data.porStatus[gmud.status] || 0) + 1;

        // Contar por impacto
        data.porImpacto[gmud.impacto_risco] = (data.porImpacto[gmud.impacto_risco] || 0) + 1;

        // Contar por mês
        const mes = new Date(gmud.data_prevista).toLocaleDateString('pt-BR', {
          month: 'short',
          year: 'numeric',
        });
        data.porMes[mes] = (data.porMes[mes] || 0) + 1;
      });

      // Calcular tempo médio de execução
      const gmudsConcluidos = gmuds.filter(g => g.status === 'concluido');
      if (gmudsConcluidos.length > 0) {
        const tempoTotal = gmudsConcluidos.reduce((acc, gmud) => {
          if (gmud.data_execucao && gmud.data_prevista) {
            const inicio = new Date(gmud.data_prevista);
            const fim = new Date(gmud.data_execucao);
            return acc + (fim.getTime() - inicio.getTime()) / (1000 * 60 * 60 * 24);
          }
          return acc;
        }, 0);
        data.tempoMedioExecucao = tempoTotal / gmudsConcluidos.length;
      }

      setRelatorioData(data);
    } catch (error) {
      console.error('Erro ao carregar relatórios:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportarRelatorio = async (formato: 'csv' | 'pdf') => {
    try {
      if (formato === 'csv') {
        // Verificar se relatorioData está disponível
        if (!relatorioData) {
          console.error('Dados de relatório não disponíveis para exportação');
          return;
        }

        // Preparar dados para CSV
        const csvData = [];

        // Adicionar resumo geral
        csvData.push(['Métrica', 'Valor']);
        csvData.push(['Total de GMUDs', relatorioData.totalGMUDs]);
        csvData.push([
          'Tempo Médio de Execução (dias)',
          relatorioData.tempoMedioExecucao.toFixed(2),
        ]);
        csvData.push(['', '']);

        // Adicionar distribuição por status
        csvData.push(['Status', 'Quantidade']);
        Object.entries(relatorioData.porStatus).forEach(([status, count]) => {
          csvData.push([status, count]);
        });
        csvData.push(['', '']);

        // Adicionar distribuição por impacto
        csvData.push(['Impacto/Risco', 'Quantidade']);
        Object.entries(relatorioData.porImpacto).forEach(([impacto, count]) => {
          csvData.push([impacto, count]);
        });
        csvData.push(['', '']);

        // Adicionar distribuição por mês
        csvData.push(['Mês', 'Quantidade']);
        Object.entries(relatorioData.porMes).forEach(([mes, count]) => {
          csvData.push([mes, count]);
        });

        // Converter para CSV
        const csvContent = csvData.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

        // Criar e baixar arquivo
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute(
          'download',
          `relatorio_gmud_${new Date().toISOString().split('T')[0]}.csv`
        );
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        console.log('Relatório CSV exportado com sucesso!');
      } else {
        console.log('Exportação PDF ainda não implementada');
      }
    } catch (error) {
      console.error('Erro ao exportar relatório:', error);
    }
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-64'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600'></div>
      </div>
    );
  }

  if (!relatorioData) {
    return (
      <div className='text-center py-12'>
        <AlertTriangle className='mx-auto h-12 w-12 text-gray-400' />
        <h3 className='mt-2 text-sm font-medium text-gray-900'>Erro ao carregar relatórios</h3>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-2xl font-bold text-gray-900'>Relatórios</h1>
          <p className='text-gray-600'>Análise e estatísticas do sistema GMUD</p>
        </div>
        <div className='flex items-center space-x-3'>
          <select
            value={filtroPeriodo}
            onChange={e => setFiltroPeriodo(e.target.value)}
            className='border border-gray-300 rounded-md px-3 py-2 text-sm'
          >
            <option value='7dias'>Últimos 7 dias</option>
            <option value='30dias'>Últimos 30 dias</option>
            <option value='90dias'>Últimos 90 dias</option>
            <option value='1ano'>Último ano</option>
          </select>
          <button
            onClick={() => exportarRelatorio('csv')}
            className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700'
          >
            <Download className='h-4 w-4 mr-2' />
            Exportar CSV
          </button>
        </div>
      </div>

      {/* Cards de resumo */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
        <div className='bg-white p-6 rounded-lg shadow'>
          <div className='flex items-center'>
            <div className='p-2 bg-blue-100 rounded-lg'>
              <BarChart3 className='h-6 w-6 text-blue-600' />
            </div>
            <div className='ml-4'>
              <p className='text-sm font-medium text-gray-600'>Total de GMUDs</p>
              <p className='text-2xl font-bold text-gray-900'>{relatorioData.totalGMUDs}</p>
            </div>
          </div>
        </div>

        <div className='bg-white p-6 rounded-lg shadow'>
          <div className='flex items-center'>
            <div className='p-2 bg-green-100 rounded-lg'>
              <TrendingUp className='h-6 w-6 text-green-600' />
            </div>
            <div className='ml-4'>
              <p className='text-sm font-medium text-gray-600'>Tempo Médio Execução</p>
              <p className='text-2xl font-bold text-gray-900'>
                {relatorioData.tempoMedioExecucao.toFixed(1)} dias
              </p>
            </div>
          </div>
        </div>

        <div className='bg-white p-6 rounded-lg shadow'>
          <div className='flex items-center'>
            <div className='p-2 bg-yellow-100 rounded-lg'>
              <AlertTriangle className='h-6 w-6 text-yellow-600' />
            </div>
            <div className='ml-4'>
              <p className='text-sm font-medium text-gray-600'>GMUDs Críticos</p>
              <p className='text-2xl font-bold text-gray-900'>
                {relatorioData.porImpacto['critico'] || 0}
              </p>
            </div>
          </div>
        </div>

        <div className='bg-white p-6 rounded-lg shadow'>
          <div className='flex items-center'>
            <div className='p-2 bg-purple-100 rounded-lg'>
              <Calendar className='h-6 w-6 text-purple-600' />
            </div>
            <div className='ml-4'>
              <p className='text-sm font-medium text-gray-600'>Período</p>
              <p className='text-2xl font-bold text-gray-900'>
                {filtroPeriodo === '30dias' ? '30 dias' : filtroPeriodo}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Gráficos e tabelas */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        {/* Distribuição por Status */}
        <div className='bg-white p-6 rounded-lg shadow'>
          <h3 className='text-lg font-medium text-gray-900 mb-4'>Distribuição por Status</h3>
          <div className='space-y-3'>
            {Object.entries(relatorioData.porStatus).map(([status, count]) => (
              <div key={status} className='flex items-center justify-between'>
                <span className='text-sm font-medium text-gray-600 capitalize'>{status}</span>
                <span className='text-sm font-bold text-gray-900'>{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Distribuição por Impacto */}
        <div className='bg-white p-6 rounded-lg shadow'>
          <h3 className='text-lg font-medium text-gray-900 mb-4'>Distribuição por Impacto</h3>
          <div className='space-y-3'>
            {Object.entries(relatorioData.porImpacto).map(([impacto, count]) => (
              <div key={impacto} className='flex items-center justify-between'>
                <span className='text-sm font-medium text-gray-600 capitalize'>{impacto}</span>
                <span className='text-sm font-bold text-gray-900'>{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Relatorios;
